import React, { useEffect, useCallback, useState } from 'react';
import { Page } from 'src/components';
import { TaptapReportTable } from '../StatusTable';
import { TaptapReportToolbar } from '../Toolbar';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { Container, Typography, makeStyles } from '@material-ui/core';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { convertToLocalISO } from 'src/utils';
import { useSnackBar } from 'src/hooks';
import { cloneDeep } from 'lodash';
import { getEODReportQueryBuilder } from 'src/redux/slices/taptap-customer';
import { TapTapCustomerData } from 'src/redux/slices/taptap-customer/types';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  productCard: {
    height: '100%'
  }
}));

const {
  selectors: taptapCustomerSelectors,
  actions: taptapCustomerActions
} = slices.taptapCustomer;

const TaptapReportView = () => {
  const classes = useStyles();
  const snackBar = useSnackBar();
  const dispatch = useAppDispatch();
  const [tableData, setTableData] = useState<TapTapCustomerData[]>([]);
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);

  const reports = useAppSelector(taptapCustomerSelectors.selectReports);
  const reportIsLoading = useAppSelector(
    taptapCustomerSelectors.selectReportsLoading
  );

  const taptapReportPayload = useAppSelector(
    taptapCustomerSelectors.selectReportsPayload
  );

  const onChangeDate = useCallback(
    (date?: MaterialUiPickersDate, field?: string) => {
      if (!field) return;

      const formattedDate = date
        ? convertToLocalISO(date, field === 'to')
        : null;

      if (field === 'from') setDateFrom(formattedDate);
      if (field === 'to') setDateTo(formattedDate);
    },
    []
  );

  const onClear = () => {
    onChangeDate(null, 'from');
    onChangeDate(null, 'to');
    setTableData([]);
    setIsFiltered(false);
  };

  const getReportsByDateRange = useCallback(async () => {
    setIsFilterLoading(true);
    if (!taptapReportPayload) return;

    const filterPayload = cloneDeep(taptapReportPayload);

    filterPayload.filters = {
      ...filterPayload.filters,
      conditions: filterPayload.filters.conditions.concat([
        ...(dateFrom
          ? [{ field: 'created_at', operator: 'gte', value: dateFrom }]
          : []),
        ...(dateTo
          ? [{ field: 'created_at', operator: 'lt', value: dateTo }]
          : [])
      ])
    };

    const response = await getEODReportQueryBuilder({
      payloadArg: filterPayload
    });

    if (response.error) {
      setIsFilterLoading(false);
      snackBar.show({
        useSound: true,
        severity: 'error',
        message:
          response.error.message ?? 'Failed at getting Taptap report data'
      });
      setTableData([]);

      return;
    }
    const data = response.data || [];
    if (data.length === 0) {
      setIsFiltered(true);
      snackBar.show({
        useSound: true,
        severity: 'info',
        message: 'No EOD report found for the selected date range.'
      });
    }

    setIsFilterLoading(false);
    setTableData(data);
    setIsFiltered(true);
  }, [taptapReportPayload, dateFrom, dateTo, snackBar]);

  useEffect(() => {
    dispatch(taptapCustomerActions.taptapFetchCustomersThunk());
  }, [dispatch]);

  useEffect(() => {
    dispatch(taptapCustomerActions.taptapFetchEODReportThunk());
  }, [dispatch]);

  return (
    <Page title="Taptap Status Report" className={classes.root}>
      <Container maxWidth={false} style={{ position: 'relative' }}>
        <Typography color="textPrimary" gutterBottom variant="h3">
          Taptap Status Report
        </Typography>
        <TaptapReportToolbar
          dateTo={dateTo}
          onClear={onClear}
          dateFrom={dateFrom}
          onChangeDate={onChangeDate}
          isFilterLoading={isFilterLoading}
          getCustomersByDateRange={getReportsByDateRange}
        />
        <Alert severity="info" style={{ marginTop: '1rem' }}>
          <Typography variant="subtitle2">
            The total count is based on these statuses:
          </Typography>
          <Typography variant="subtitle2">
            <strong>Total Completed</strong> - COMPLETED
          </Typography>
          <Typography variant="subtitle2">
            <strong>Total On Hold</strong> - HOLD, CANCELLED
          </Typography>
          <Typography variant="subtitle2">
            <strong>Total Ongoing</strong> - BUILDING, ONGOING DELIVERY,
            TESTING, READY FOR DELIVERY, READY FOR PICKUP
          </Typography>
          <Typography variant="subtitle2">
            <strong>Total Pending</strong> - FOR PAYMENT, FOR FULLPAYMENT, FOR
            CHECKING, FOR PACK, PREPARING ITEMS, ON QUEUE, DONE PAYMENT
          </Typography>
        </Alert>
        <TaptapReportTable
          reports={!isFiltered ? reports : tableData}
          isLoading={reportIsLoading}
        />
      </Container>
    </Page>
  );
};

export default TaptapReportView;
